import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useState } from "react";

// Models
import { RootState } from "@/redux/store";
import { IAdministrationTools, IDmsControlType } from "@/Settings/types/administrationTools/model";

// API
import { updateAdministrationTools } from "@/Settings/api/AdministrationTools/mutations";

// MUI
import { Box, Typography, Stack } from "@mui/material";

// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";

type ControlTypeValue = {
	value: string;
};

const DASHED_LINE = "-------";
const EMPTY_CONTROL_TYPE_OPTION: ControlTypeValue = { value: DASHED_LINE };

const REYNOLDS_CIT_CONTROL_TYPES = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
const allControlTypeValues = REYNOLDS_CIT_CONTROL_TYPES.map<ControlTypeValue>((value) => ({ value }));

const allControlTypesOptions = [
	{ identifier: "customerNumber", label: "Customer Number", value: "" },
	{ identifier: "vendorNumber", label: "Vendor Number", value: "" },
	{ identifier: "employeeNumber", label: "Employee Number", value: "" },
	{ identifier: "stockNumber", label: "Stock Number", value: "" },
	{ identifier: "repairOrder", label: "Repair Order", value: "" },
	{ identifier: "dealNumber", label: "Deal Number", value: "" },
	{ identifier: "serialNumber", label: "Serial Number", value: "" },
	{ identifier: "leaseNumber", label: "Lease Number", value: "" },
	{ identifier: "miscellaneous", label: "Miscellaneous", value: "" },
];

export default function CITControlTypes(props: Readonly<{ settings: IAdministrationTools }>) {
	const { settings } = props;

	const queryClient = useQueryClient();
	const locationId = useSelector((state: RootState) => state.location.location?.locationId ?? "");
	const [controlTypeUpdating, setControlTypeUpdating] = useState<string | null>(null);

	const updateCachedValue = (newData: Partial<IAdministrationTools> | null | undefined) => {
		queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => ({
			...cachedData,
			integrations: {
				...cachedData?.integrations,
				dmsControlTypes: newData?.integrations?.dmsControlTypes,
			},
		}));
	};

	const { mutate: mutateControlTypes, isPending: isLoading } = useMutation({
		mutationKey: ["administrationToolsDMSControlTypeMutation", locationId],
		mutationFn: (input: Partial<IAdministrationTools>) => updateAdministrationTools(locationId, input),
		onError: (error) => errorLogger(error.message),
		onSuccess: updateCachedValue,
		onSettled: () => setControlTypeUpdating(null),
	});

	const getControlTypes = () => {
		const fetchedControlTypes = settings?.integrations?.dmsControlTypes ?? [];
		return fetchedControlTypes.length ? fetchedControlTypes : allControlTypesOptions;
	};

	const getAvailableOptionsToChooseFrom = () => {
		const alreadyChosenOptions = getControlTypes().map((controlType) => controlType.value);
		return [...allControlTypeValues].filter((controlType) => !alreadyChosenOptions.includes(controlType.value));
	};

	const handleChange = (selected: TSelectValue, identifier: string) => {
		const { value: selectedValue } = selected as ControlTypeValue;
		setControlTypeUpdating(identifier);

		const updatedDmsControlTypes = getControlTypes().map((controlType) =>
			controlType.identifier === identifier ? { ...controlType, value: selectedValue } : controlType
		);

		const input = {
			integrations: {
				...settings?.integrations,
				dmsControlTypes: updatedDmsControlTypes,
			},
		};

		mutateControlTypes(input);
	};

	const isLoadingOrSavingControlType = (str: string) => isLoading && controlTypeUpdating === str;

	const commonProperties = (controlType: IDmsControlType) => {
		const selectedValue = controlType.value && controlType.value !== DASHED_LINE ? controlType : null;

		const options = [EMPTY_CONTROL_TYPE_OPTION, ...(selectedValue ? [selectedValue] : []), ...getAvailableOptionsToChooseFrom()];

		const isLoadingOrSaving = isLoadingOrSavingControlType(controlType.identifier);

		return {
			loading: isLoadingOrSaving,
			readOnly: isLoadingOrSaving,
			disabled: isLoading,
			options,
		};
	};

	const renderContent = () => {
		return getControlTypes().map((controlType, idx) => {
			return (
				<Stack
					mt={"11px"}
					display={"flex"}
					key={`${controlType.identifier}_${idx}`}
					direction={"row"}
					alignItems={"center"}
					justifyContent={"space-between"}
				>
					<Typography className="!font-sans !text-[14.5px] text-default">{controlType.label}</Typography>
					<Box>
						<SmartSelector
							selectBy={"value"}
							showBy={"value"}
							selected={controlType}
							inputProps={{
								placeholder: "Select Control Type",
							}}
							sx={{ minWidth: "90px" }}
							onChangeAction={(option) => handleChange(option, controlType.identifier)}
							{...commonProperties(controlType)}
						/>
					</Box>
				</Stack>
			);
		});
	};

	return (
		<Stack display={"flex"} direction={"column"} width={258} className="!my-[32px]">
			<Typography className="!font-montserrat !text-default !text-[20px] !font-medium">CIT Control Type</Typography>
			<Stack display={"flex"} direction={"column"}>
				{renderContent()}
			</Stack>
		</Stack>
	);
}
